/**
 * Loading plugin for the in-house dva replacement (ENG-4570).
 *
 * Direct port of dva-loading: tracks per-effect / per-model / global pending
 * state in a `loading` slice via the onEffect hook, honoring `only`/`except`.
 */
import { Reducer } from 'redux'

const SHOW = '@@DVA_LOADING/SHOW'
const HIDE = '@@DVA_LOADING/HIDE'
const NAMESPACE = 'loading'

export interface LoadingState {
  global: boolean
  models: Record<string, boolean>
  effects: Record<string, boolean>
}

interface LoadingOpts {
  namespace?: string
  only?: string[]
  except?: string[]
}

const createLoading = (opts: LoadingOpts = {}) => {
  const namespace = opts.namespace || NAMESPACE
  const { only = [], except = [] } = opts

  if (only.length > 0 && except.length > 0) {
    throw new Error('It is ambiguous to configure `only` and `except` items at the same time.')
  }

  const initialState: LoadingState = { global: false, models: {}, effects: {} }

  const reducer: Reducer<LoadingState, any> = (state = initialState, { type, payload }) => {
    const { namespace: modelNamespace, actionType } = payload || {}
    switch (type) {
      case SHOW:
        return {
          ...state,
          global: true,
          models: { ...state.models, [modelNamespace]: true },
          effects: { ...state.effects, [actionType]: true },
        }
      case HIDE: {
        const effects: Record<string, boolean> = { ...state.effects, [actionType]: false }
        const models: Record<string, boolean> = {
          ...state.models,
          [modelNamespace]: Object.keys(effects).some((key) => {
            if (key.split('/')[0] !== modelNamespace) {
              return false
            }
            return effects[key]
          }),
        }
        const global = Object.keys(models).some((key) => models[key])
        return { ...state, global, models, effects }
      }
      default:
        return state
    }
  }

  const extraReducers = { [namespace]: reducer }

  const onEffect = (
    effect: any,
    { put }: any,
    model: { namespace: string },
    actionType: string,
  ) => {
    const { namespace: modelNamespace } = model
    const tracked =
      (only.length === 0 && except.length === 0) ||
      (only.length > 0 && only.indexOf(actionType) !== -1) ||
      (except.length > 0 && except.indexOf(actionType) === -1)

    if (!tracked) {
      return effect
    }

    return function* (...args: any[]) {
      yield put({ type: SHOW, payload: { namespace: modelNamespace, actionType } })
      try {
        yield effect(...args)
      } finally {
        // try/finally (a hardening over dva-loading) guarantees HIDE runs even
        // if the effect is cancelled — e.g. a takeLatest watcher being
        // superseded — so the loading flag can't get stuck `true`.
        yield put({ type: HIDE, payload: { namespace: modelNamespace, actionType } })
      }
    }
  }

  return { extraReducers, onEffect }
}

export default createLoading
